import React, { useState } from 'react';
import { Clock, TrendingUp, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTotalTimeSaved } from '../hooks/useTotalTimeSaved';

interface TotalTimeSavedProps {
  className?: string;
}

const TotalTimeSaved: React.FC<TotalTimeSavedProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const { formattedTime, summaryCount, resetTimeSaved, getStats } =
    useTotalTimeSaved();
  const [showDetails, setShowDetails] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const stats = getStats();

  const handleReset = () => {
    resetTimeSaved();
    setShowResetConfirm(false);
    setShowDetails(false);
  };

  if (stats.totalTimeSaved === 0) {
    return null; // Ne pas afficher si aucun temps gagné
  }

  return (
    <div className={`relative ${className}`}>
      {/* Badge principal */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="flex items-center space-x-2 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg px-3 py-2 transition-colors duration-200 group"
        title="Temps total gagné"
      >
        <Clock className="w-4 h-4 text-green-600" />
        <span className="text-sm font-medium text-green-700">
          {formattedTime}
        </span>
        <TrendingUp className="w-3 h-3 text-green-500" />
      </button>

      {/* Détails déroulants */}
      {showDetails && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
          <div className="space-y-3">
            {/* En-tête */}
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">
                {t('summary.result.stats.timeSaved')}
              </h3>
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            {/* {t('trophies.categories.stats')} */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total gagné:</span>
                <span className="text-sm font-medium text-green-600">
                  {formattedTime}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Résumés créés:</span>
                <span className="text-sm font-medium text-blue-600">
                  {summaryCount}
                </span>
              </div>

              {summaryCount > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    Moyenne par résumé:
                  </span>
                  <span className="text-sm font-medium text-purple-600">
                    {Math.round(stats.averageTimePerSummary)}s
                  </span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="pt-2 border-t border-gray-100">
              {!showResetConfirm ? (
                <button
                  onClick={() => setShowResetConfirm(true)}
                  className="flex items-center space-x-2 text-sm text-red-600 hover:text-red-700 transition-colors"
                >
                  <RotateCcw className="w-3 h-3" />
                  <span>Réinitialiser</span>
                </button>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-gray-600">
                    Êtes-vous sûr de vouloir réinitialiser ?
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleReset}
                      className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    >
                      Confirmer
                    </button>
                    <button
                      onClick={() => setShowResetConfirm(false)}
                      className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TotalTimeSaved;
